/** EDP-00006 — Role Experience framework (mirrors pkg/rolexp) */

export const EDP_RX_ID = 'EDP-00006';

export type Philosophy =
  | 'role_first'
  | 'decision_first'
  | 'minimal_noise'
  | 'maximum_context'
  | 'action_oriented'
  | 'ai_guided'
  | 'business_outcome_focused'
  | 'explain_before_execute';

export type PlatformRole =
  | 'organization_owner'
  | 'brand_owner'
  | 'ceo'
  | 'coo'
  | 'cmo'
  | 'finance_manager'
  | 'advertising_manager'
  | 'listing_manager'
  | 'content_team'
  | 'creative_team'
  | 'operations_team'
  | 'inventory_planner'
  | 'warehouse_team'
  | 'customer_support'
  | 'analyst'
  | 'agency_owner'
  | 'agency_executive'
  | 'administrator'
  | 'developer'
  | 'auditor';

export type DashboardType =
  | 'executive'
  | 'brand'
  | 'advertising'
  | 'catalog'
  | 'inventory'
  | 'finance'
  | 'operations'
  | 'customer'
  | 'agency'
  | 'admin';

export type KPICategory =
  | 'revenue'
  | 'profit'
  | 'roas'
  | 'acos'
  | 'tacos'
  | 'ctr'
  | 'conversion'
  | 'sessions'
  | 'traffic'
  | 'inventory_health'
  | 'cash_flow'
  | 'business_health'
  | 'customer_health'
  | 'brand_health'
  | 'operational_health';

export type AlertCategory =
  | 'critical'
  | 'warning'
  | 'opportunity'
  | 'information'
  | 'ai_suggestion'
  | 'marketplace_event'
  | 'team_mention';

export interface RoleSpec {
  role: PlatformRole;
  label: string;
  dashboard: DashboardType;
  default_home?: string;
  kpis?: KPICategory[];
  ai_features?: string[];
}

export interface DecisionItem {
  id: string;
  title: string;
  category: string;
  impact?: string;
  confidence?: number;
  requires_approval: boolean;
  source_module?: string;
}

export interface AlertItem {
  id: string;
  category: AlertCategory;
  title: string;
  message?: string;
  module?: string;
  created_at: string;
}

export const PHILOSOPHIES: { id: Philosophy; label: string; description: string }[] = [
  { id: 'role_first', label: 'Role First', description: 'Experience tailored to role responsibilities' },
  { id: 'decision_first', label: 'Decision First', description: 'Decision center prominent; actions over reports' },
  { id: 'minimal_noise', label: 'Minimal Noise', description: 'Only role-relevant KPIs and alerts' },
  { id: 'maximum_context', label: 'Maximum Context', description: 'Scope, trends, and comparisons inline' },
  { id: 'action_oriented', label: 'Action Oriented', description: 'Every insight links to a next action' },
  { id: 'ai_guided', label: 'AI Guided', description: 'Role-specific AI briefing and recommendations' },
  { id: 'business_outcome_focused', label: 'Business Outcome Focused', description: 'KPIs framed as business outcomes' },
  { id: 'explain_before_execute', label: 'Explain Before Execute', description: 'Preview before consequential decisions' },
];

export const DASHBOARD_TYPES: { id: DashboardType; label: string }[] = [
  { id: 'executive', label: 'Executive Dashboard' },
  { id: 'brand', label: 'Brand Dashboard' },
  { id: 'advertising', label: 'Advertising Dashboard' },
  { id: 'catalog', label: 'Catalog Dashboard' },
  { id: 'inventory', label: 'Inventory Dashboard' },
  { id: 'finance', label: 'Finance Dashboard' },
  { id: 'operations', label: 'Operations Dashboard' },
  { id: 'customer', label: 'Customer Dashboard' },
  { id: 'agency', label: 'Agency Dashboard' },
  { id: 'admin', label: 'Admin Dashboard' },
];

export const KPI_LIBRARY: { id: KPICategory; label: string }[] = [
  { id: 'revenue', label: 'Revenue' },
  { id: 'profit', label: 'Profit' },
  { id: 'roas', label: 'ROAS' },
  { id: 'acos', label: 'ACOS' },
  { id: 'tacos', label: 'TACOS' },
  { id: 'ctr', label: 'CTR' },
  { id: 'conversion', label: 'Conversion' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'traffic', label: 'Traffic' },
  { id: 'inventory_health', label: 'Inventory Health' },
  { id: 'cash_flow', label: 'Cash Flow' },
  { id: 'business_health', label: 'Business Health' },
  { id: 'customer_health', label: 'Customer Health' },
  { id: 'brand_health', label: 'Brand Health' },
  { id: 'operational_health', label: 'Operational Health' },
];

export const PERSONAL_SECTIONS = [
  'My Dashboard', 'My Tasks', 'My Recommendations', 'My Alerts',
  'My Activity', 'My Calendar', 'My Approvals', 'My Learning', 'My Bookmarks',
];

export const AI_FEATURES = [
  'Daily Briefing', 'Recommendations', 'Alerts', 'Insights', 'Forecasts',
  'Tasks', 'Learning', 'Simulation', 'Approvals',
];

export const BRIEFING_SCHEDULE = [
  'Morning Brief', 'Afternoon Review', 'End-of-Day Summary',
  'Weekly Executive Review', 'Monthly Business Review', 'Quarterly Business Review',
];

export const ROLE_SPECS: RoleSpec[] = [
  { role: 'ceo', label: 'CEO', dashboard: 'executive', default_home: '/command', kpis: ['revenue', 'profit', 'business_health', 'brand_health', 'roas'] },
  { role: 'brand_owner', label: 'Brand Owner', dashboard: 'brand', default_home: '/command', kpis: ['revenue', 'brand_health', 'roas', 'business_health'] },
  { role: 'advertising_manager', label: 'Advertising Manager', dashboard: 'advertising', default_home: '/amazon/revenue-ops', kpis: ['roas', 'acos', 'tacos', 'ctr', 'revenue'] },
  { role: 'finance_manager', label: 'Finance Manager', dashboard: 'finance', default_home: '/amazon/finance-ops', kpis: ['revenue', 'profit', 'cash_flow', 'business_health'] },
  { role: 'listing_manager', label: 'Listing Manager', dashboard: 'catalog', default_home: '/amazon/catalog-ops', kpis: ['ctr', 'conversion', 'sessions', 'brand_health'] },
  { role: 'inventory_planner', label: 'Inventory Planner', dashboard: 'inventory', default_home: '/amazon/inventory-intel', kpis: ['inventory_health', 'operational_health'] },
  { role: 'operations_team', label: 'Operations Team', dashboard: 'operations', default_home: '/amazon/order-ops', kpis: ['operational_health', 'inventory_health'] },
  { role: 'customer_support', label: 'Customer Support', dashboard: 'customer', default_home: '/amazon/post-sales-ops', kpis: ['customer_health', 'operational_health'] },
  { role: 'agency_owner', label: 'Agency Owner', dashboard: 'agency', default_home: '/command', kpis: ['revenue', 'business_health', 'brand_health'] },
  { role: 'administrator', label: 'Administrator', dashboard: 'admin', default_home: '/admin', kpis: ['operational_health', 'business_health'] },
];

export const DEMO_DECISIONS: DecisionItem[] = [
  { id: 'dec-1', title: 'Approve SP budget increase +20%', category: 'high_impact', impact: '+$4,200/month sales', confidence: 0.91, requires_approval: true, source_module: 'revenueops' },
  { id: 'dec-2', title: 'File FBA reimbursement — 3 lost units', category: 'critical', impact: 'Recover $89.97', confidence: 0.94, requires_approval: true, source_module: 'financeops' },
  { id: 'dec-3', title: 'Refresh CASE-PRO listing title', category: 'optimization', impact: '+8% organic sessions', confidence: 0.82, requires_approval: false, source_module: 'contentintel' },
];

export const DEMO_ALERTS: AlertItem[] = [
  { id: 'al-1', category: 'critical', title: 'Stockout risk — EARBUDS-BLK-001', message: '12 days cover remaining', module: 'inventoryintel', created_at: new Date().toISOString() },
  { id: 'al-2', category: 'opportunity', title: 'Weekend ROAS 7.2 vs weekday 5.1', message: 'Dayparting opportunity', module: 'revenueops', created_at: new Date(Date.now() - 3600000).toISOString() },
  { id: 'al-3', category: 'warning', title: 'Settlement mismatch — Feb payout', message: '$127.40 unreconciled', module: 'financeops', created_at: new Date(Date.now() - 10800000).toISOString() },
  { id: 'al-4', category: 'ai_suggestion', title: '3 listings below SEO threshold', message: 'AI recommends title optimization', module: 'contentintel', created_at: new Date(Date.now() - 18000000).toISOString() },
];

export const DEMO_BRIEFING = {
  greeting: "Good morning — here's your commerce briefing",
  sections: [
    { title: 'Marketplace Insights', priority: 'high', summary: '3 listings show CTR decline; 2 SKUs at risk' },
    { title: 'Advertising Performance', priority: 'normal', summary: 'Portfolio ROAS 5.8; 2 campaigns budget-constrained' },
    { title: 'Pending Decisions', priority: 'high', summary: '3 approvals awaiting action' },
  ],
};

export function buildRoleExpDashboard() {
  return {
    supported_roles: ROLE_SPECS.length,
    dashboard_types: DASHBOARD_TYPES.length,
    kpi_categories: KPI_LIBRARY.length,
    personal_sections: PERSONAL_SECTIONS.length,
    alert_categories: 7,
    briefing_types: BRIEFING_SCHEDULE.length,
  };
}

export function resolveRoleSpec(role: PlatformRole): RoleSpec | undefined {
  return ROLE_SPECS.find((r) => r.role === role);
}

export function kpiLabel(kpi: KPICategory): string {
  return KPI_LIBRARY.find((k) => k.id === kpi)?.label ?? kpi.replace(/_/g, ' ');
}

export function dashboardLabel(d: DashboardType): string {
  return DASHBOARD_TYPES.find((t) => t.id === d)?.label ?? d;
}
